import { useMemo, useState, type FormEvent } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { errorText, type Attributes, type Gender, type HeroCreation, type StartPath } from '@mezha/api'
import { useCreateHeroMutation, useCreationQuery } from '../../api/endpoints/hero'
import { useMeQuery } from '../../api/endpoints/auth'
import { fromFailure, type ApiFailure } from '../../api/baseApi'
import { Emblem } from '../../ui/brand/Emblems'
import { PlankButton, cx } from '../../ui'
import { Failure, Loading } from '../../ui/State'
import { ATTRS, FAMILY_RU, ItemArt, modList } from '../common'
import { AuthFrame, Field, FormError } from './AuthFrame'

type Key = (typeof ATTRS)[number]['key']
const CUSTOM = 'custom'

/** `/hero/new`: name, gender and avatar, and the start — one of the ready paths
 *  (attributes and a training weapon of a weapon family) or a start by hand.
 *  Whether the name is taken is known only from the server. */
export function HeroNew() {
  const me = useMeQuery()
  const creation = useCreationQuery()

  if (me.data?.has_hero) return <Navigate to="/play" replace />
  return (
    <AuthFrame title="Новый герой" sub="Кем вы выйдете к Меже" wide="creation">
      {(me.isLoading || creation.isLoading) && <Loading />}
      {me.isError && <Failure error={me.error} retry={() => void me.refetch()} what="Данные учётной записи" />}
      {creation.isError && <Failure error={creation.error} retry={() => void creation.refetch()} what="Варианты героя" />}
      {me.data && creation.data && <CreationForm c={creation.data} />}
    </AuthFrame>
  )
}

function CreationForm({ c }: { c: HeroCreation }) {
  const [name, setName] = useState('')
  const [gender, setGender] = useState<Gender>('male')
  const [avatar, setAvatar] = useState(() => c.avatars.find(a => a.gender === 'male')?.id ?? '')
  const [pathId, setPathId] = useState(c.paths[0]?.id ?? CUSTOM)
  const [custom, setCustom] = useState<Attributes>(() => even(c))
  const [weaponId, setWeaponId] = useState(c.paths[0]?.weapon.id ?? '')
  const [fieldError, setFieldError] = useState<{ field: string; text: string } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [create, { isLoading }] = useCreateHeroMutation()
  const navigate = useNavigate()

  const avatars = c.avatars.filter(a => a.gender === gender)
  const path = c.paths.find(p => p.id === pathId) ?? null
  const attrs = path ? path.attributes : custom
  const weapon = path ? path.weapon : c.paths.find(p => p.weapon.id === weaponId)?.weapon ?? null
  const spent = ATTRS.reduce((s, a) => s + custom[a.key], 0)
  const left = c.start_total - spent
  const trimmed = name.trim().replace(/\s+/g, ' ')
  const ready = trimmed.length >= 3 && trimmed.length <= 20 && Boolean(avatar) && (path !== null || (left === 0 && weapon !== null))
  const weapons = useMemo(() => c.paths.map(p => p.weapon), [c.paths])

  const pickGender = (g: Gender) => {
    setGender(g)
    setAvatar(c.avatars.find(a => a.gender === g)?.id ?? '')
  }
  const bump = (k: Key, d: number) => setCustom(a => ({ ...a, [k]: Math.min(c.start_attribute_max, Math.max(c.attribute_min, a[k] + d)) }))

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!ready || isLoading) return
    setError(null)
    setFieldError(null)
    try {
      await create(path
        ? { name: trimmed, gender, avatar_id: avatar, path_id: path.id }
        : { name: trimmed, gender, avatar_id: avatar, path_id: CUSTOM, attributes: custom, weapon_id: weaponId }).unwrap()
      navigate('/play', { replace: true })
    } catch (err) {
      const f = fromFailure(err as ApiFailure)
      if (f.code === 'hero_exists') navigate('/play', { replace: true })
      else if (f.code === 'name_taken') setFieldError({ field: 'name', text: errorText(f) })
      else if (typeof f.details.field === 'string') setFieldError({ field: f.details.field, text: errorText(f) })
      else setError(errorText(f))
    }
  }

  return (
    <form className="create" onSubmit={submit} noValidate>
      <div className="create__look">
        <Field id="hero-name" label="Имя героя" value={name} onChange={v => { setName(v); setFieldError(null) }} maxLength={20} autoFocus
          hint="От 3 до 20 знаков: буквы, цифры, пробел или дефис" error={fieldError?.field === 'name' ? fieldError.text : null} autoComplete="off" />
        <fieldset className="create__group">
          <legend>Пол</legend>
          <div className="create__genders" role="radiogroup" aria-label="Пол">
            {(['male', 'female'] as Gender[]).map(g => (
              <button key={g} type="button" role="radio" aria-checked={gender === g} className={cx('create__gender slab', gender === g && 'is-on')} onClick={() => pickGender(g)}>
                {g === 'male' ? 'Мужчина' : 'Женщина'}
              </button>
            ))}
          </div>
        </fieldset>
        <fieldset className="create__group">
          <legend>Облик</legend>
          <div className="create__avatars" role="radiogroup" aria-label="Облик">
            {avatars.map(a => (
              <button key={a.id} type="button" role="radio" aria-checked={avatar === a.id} aria-label={a.name} title={a.name}
                className={cx('create__avatar', avatar === a.id && 'is-on')} onClick={() => setAvatar(a.id)}>
                {a.portrait ? <img src={a.portrait} alt="" /> : <span className="create__noart"><Emblem name="nav.hero" size={44} /><small>{a.name}</small></span>}
              </button>
            ))}
          </div>
          {fieldError?.field === 'avatar_id' && <small className="field__error">{fieldError.text}</small>}
        </fieldset>
      </div>

      <div className="create__start">
        <fieldset className="create__group">
          <legend>Путь</legend>
          <div className="create__paths" role="radiogroup" aria-label="Путь">
            {c.paths.map(p => (
              <button key={p.id} type="button" role="radio" aria-checked={pathId === p.id} className={cx('create__path slab', pathId === p.id && 'is-on')} onClick={() => setPathId(p.id)}>
                <ItemArt item={p.weapon} size={36} /><span><b>{p.name}</b><small>{FAMILY_RU[p.weapon.family] ?? ''}</small></span>
              </button>
            ))}
            <button type="button" role="radio" aria-checked={pathId === CUSTOM} className={cx('create__path slab', pathId === CUSTOM && 'is-on')} onClick={() => setPathId(CUSTOM)}>
              <Emblem name="creation.custom" size={30} /><span><b>Свой путь</b><small>распределить самому</small></span>
            </button>
          </div>
        </fieldset>

        <section className="create__card parch" aria-label={path ? `Путь: ${path.name}` : 'Свой путь'}>
          <p className="create__about">{path ? path.description : `Распределите ${c.start_total} очков: в каждом свойстве от ${c.attribute_min} до ${c.start_attribute_max}. Оружие — учебное одного из путей.`}</p>
          <ul className="create__attrs" aria-label="Свойства">
            {ATTRS.map(a => (
              <li key={a.key}>
                <Emblem name={a.emblem} size={26} className="create__attricon" /><span className="create__attr"><b>{a.label}</b><small>{a.hint}</small></span>
                <span className="create__pips" aria-hidden="true">
                  {Array.from({ length: c.start_attribute_max }, (_, i) => <i key={i} className={i < attrs[a.key] ? 'is-on' : undefined} />)}
                </span>
                {path ? <output aria-label={a.label}>{attrs[a.key]}</output> : (
                  <span className="create__step">
                    <button type="button" aria-label={`${a.label}: меньше`} disabled={custom[a.key] <= c.attribute_min} onClick={() => bump(a.key, -1)}>−</button>
                    <output aria-label={a.label}>{custom[a.key]}</output>
                    <button type="button" aria-label={`${a.label}: больше`} disabled={custom[a.key] >= c.start_attribute_max || left <= 0} onClick={() => bump(a.key, 1)}>+</button>
                  </span>
                )}
              </li>
            ))}
          </ul>
          {!path && <p className={cx('create__left', left !== 0 && 'is-warn')} aria-live="polite">{left > 0 ? `Осталось очков: ${left}` : left < 0 ? `Лишних очков: ${-left}` : 'Все очки распределены'}</p>}
          {fieldError?.field === 'attributes' && <p className="field__error">{fieldError.text}</p>}
          {path ? weapon && <Weapon w={weapon} /> : (
            <label className="create__weapon">
              <span>Учебное оружие</span>
              <select className="slab" value={weaponId} onChange={e => setWeaponId(e.target.value)}>
                {weapons.map(w => <option key={w.id} value={w.id}>{w.name} — {FAMILY_RU[w.family] ?? ''}, {w.hands === 2 ? 'двуручное' : 'одноручное'}</option>)}
              </select>
              {weapon && <Weapon w={weapon} />}
            </label>
          )}
          {fieldError?.field === 'weapon_id' && <p className="field__error">{fieldError.text}</p>}
          <p className="create__note">Ещё на герое: {c.loadout.map(i => i.name.toLowerCase()).join(', ')}. Распределение можно переделать бесплатным сбросом свойств.</p>
        </section>
        <FormError text={error} />
        <div className="auth__actions">
          <PlankButton gold type="submit" disabled={!ready || isLoading} aria-busy={isLoading || undefined}>{isLoading ? 'Готовимся к пути…' : 'Выйти к Меже'}</PlankButton>
        </div>
      </div>
    </form>
  )
}

function Weapon({ w }: { w: StartPath['weapon'] }) {
  return (
    <div className="create__weaponcard">
      <ItemArt item={w} size={44} />
      <span><b>{w.name}</b><small>{FAMILY_RU[w.family] ?? ''} · {w.hands === 2 ? 'двуручное' : 'одноручное'}{modList(w.modifiers).map(m => ` · ${m.label.toLowerCase()} ${m.text}`).join('')}</small></span>
    </div>
  )
}

/** The start by hand begins even: the start points spread over the six attributes. */
function even(c: HeroCreation): Attributes {
  const base = Math.floor(c.start_total / 6)
  const out = { str: base, vit: base, dex: base, wis: base, lck: base, kno: base }
  let rest = c.start_total - base * 6
  for (const a of ATTRS) if (rest-- > 0) out[a.key] += 1
  return out
}
